import { useState } from 'react';
import type { DecisionDto } from '@/api/generated';
import { useChooseDecision, useDecisions, useDeleteDecision } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { formatRelativeDeadline, formatShortDate, toNumber } from '@/lib/format';
import { DecisionCreateDialog, DecisionEditDialog } from './DecisionDialogs';
import styles from './Decisions.module.css';

const PHASE_LABEL: Record<string, string> = {
  Voor: 'Voorber.',
  Ontw: 'Ontwerp',
  Verg: 'Vergunningen',
  Aanb: 'Aanbest.',
  Ruw: 'Ruwbouw',
  Wwd: 'W&W-d.',
  Tech: 'Technieken',
  Afw: 'Afwerking',
  Opl: 'Oplevering',
  Naz: 'Nazorg',
};

export function Decisions() {
  const decisions = useDecisions({ pageSize: 50 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DecisionDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DecisionDto | null>(null);
  const deleteMutation = useDeleteDecision();

  return (
    <AppFrame>
      <QueryBoundary query={decisions} loading="Beslissingen laden…">
        {(page) => {
          const items = page.items;
          const waiting = items.filter((d) => d.status === 'Open');
          const logged = items.filter((d) => d.status === 'Closed');
          const grouped = groupByPhase(logged);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Beslissingenlogboek · {toNumber(page.total)} vastgelegd · {waiting.length}{' '}
                    wachten
                  </div>
                  <h1 className={styles.title}>Beslissingen</h1>
                  <p className={styles.subtitle}>
                    Elk besluit met wie, wanneer, waarom. Voorkomt later discussies — "we hadden
                    afgesproken dat…".
                  </p>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <Btn>
                    <Icon name="filter" size={14} /> Filter
                  </Btn>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Beslissing toevoegen
                  </Btn>
                </div>
              </div>
              <DecisionCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              {editTarget && (
                <DecisionEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  decision={editTarget}
                />
              )}
              <ConfirmDialog
                open={deleteTarget !== null}
                onOpenChange={(o) => {
                  if (!o) setDeleteTarget(null);
                }}
                title={`${deleteTarget?.title ?? 'Beslissing'} verwijderen?`}
                description="De beslissing en alle vastgelegde opties verdwijnen uit het logboek."
                confirmLabel="Verwijder"
                destructive
                pending={deleteMutation.isPending}
                onConfirm={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteMutation.mutateAsync(deleteTarget.id);
                    setDeleteTarget(null);
                  } catch {
                    // toast surfaced by hook; keep dialog open so user can retry.
                  }
                }}
              />

              {waiting.length > 0 && (
                <div className={styles.waiting}>
                  <div className={`row gap-8 ${styles.waitingHead}`}>
                    <Icon name="flag" size={15} color="var(--accent-2)" />
                    <h3 className={styles.waitingTitle}>
                      Wacht op jouw beslissing — {waiting.length}
                    </h3>
                  </div>
                  <div className={styles.waitingGrid}>
                    {waiting.map((d) => (
                      <WaitingCard key={d.id} decision={d} />
                    ))}
                  </div>
                </div>
              )}

              <Card className={styles.tableCard}>
                <div className={`row gap-12 ${styles.tableToolbar}`}>
                  <div className="row gap-6">
                    <Pill kind="idle" dot={false}>
                      Alle ({toNumber(page.total)})
                    </Pill>
                    <Pill kind="ok" dot={false}>
                      Vastgelegd ({logged.length})
                    </Pill>
                    <Pill kind="accent" dot={false}>
                      Wacht ({waiting.length})
                    </Pill>
                  </div>
                  <div className={`ml-auto row gap-6 ${styles.groupBy}`}>
                    <span className={styles.groupByLabel}>Groepeer op:</span>
                    <span className={styles.groupByValue}>Fase</span>
                  </div>
                </div>

                {logged.length === 0 ? (
                  <div className="mono c-ink-3" style={{ padding: '24px' }}>
                    Nog geen vastgelegde beslissingen.
                  </div>
                ) : (
                  Array.from(grouped.entries()).map(([phase, rows], gi) => (
                    <DecisionGroup
                      key={phase}
                      phase={phase}
                      rows={rows}
                      expanded={gi === 0}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                    />
                  ))
                )}
              </Card>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

function WaitingCard({ decision }: { decision: DecisionDto }) {
  const choose = useChooseDecision();
  return (
    <div className={styles.waitingCard}>
      <div className="row gap-6">
        <Pill kind="idle" dot={false}>
          {PHASE_LABEL[decision.phaseId] ?? decision.phaseId}
        </Pill>
        <span className={`mono ml-auto ${styles.waitingDue}`}>
          {formatRelativeDeadline(decision.deadline)}
        </span>
      </div>
      <div className={styles.waitingName}>{decision.title}</div>
      <div className={`col gap-6 ${styles.waitingOptions}`}>
        {decision.options.map((opt) => (
          <button
            type="button"
            key={opt}
            className={styles.waitingOptionBtn}
            onClick={() => choose.mutate({ id: decision.id, chosen: opt })}
            disabled={choose.isPending}
          >
            <span className={styles.waitingOptionDot} aria-hidden="true" />
            <span className={styles.waitingOptionLabel}>{opt}</span>
            <span className={styles.waitingOptionAction}>Kies</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DecisionGroup({
  phase,
  rows,
  expanded,
  onEdit,
  onDelete,
}: {
  phase: string;
  rows: DecisionDto[];
  expanded: boolean;
  onEdit: (d: DecisionDto) => void;
  onDelete: (d: DecisionDto) => void;
}) {
  return (
    <>
      <div className={expanded ? styles.groupHeader : styles.groupHeaderCollapsed}>
        <div className="row gap-8">
          <Icon name={expanded ? 'chevD' : 'chevR'} size={12} color="var(--ink-3)" />
          <span className={styles.groupName}>{PHASE_LABEL[phase] ?? phase}</span>
          <span className={`mono ${styles.groupCount}`}>{rows.length} beslissingen</span>
        </div>
      </div>
      {expanded &&
        rows.map((d) => (
          <DecisionEntry key={d.id} decision={d} onEdit={onEdit} onDelete={onDelete} />
        ))}
    </>
  );
}

function DecisionEntry({
  decision,
  onEdit,
  onDelete,
}: {
  decision: DecisionDto;
  onEdit: (d: DecisionDto) => void;
  onDelete: (d: DecisionDto) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className={`row gap-16 ${styles.entry}`}>
      <div className={styles.entryDot}>
        <Icon name="check" size={14} strokeWidth={2.5} />
      </div>
      <div className="flex-1">
        <div className={`row gap-10 ${styles.entryHead}`}>
          <div className={styles.entryTitle}>{decision.title}</div>
          <Pill kind="idle" dot={false}>
            {PHASE_LABEL[decision.phaseId] ?? decision.phaseId}
          </Pill>
          <span className={`mono ml-auto ${styles.entryDate}`}>
            {formatShortDate(decision.deadline)}
          </span>
          <div className={styles.kebabWrap}>
            <button
              type="button"
              className={styles.kebabBtn}
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Meer acties"
            >
              <Icon name="more" size={14} />
            </button>
            {menuOpen && (
              <>
                <button
                  type="button"
                  className={styles.kebabBackdrop}
                  aria-label="Sluit menu"
                  tabIndex={-1}
                  onClick={() => setMenuOpen(false)}
                />
                <div role="menu" className={styles.kebabMenu}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(decision);
                    }}
                  >
                    Bewerk
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="c-bad"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete(decision);
                    }}
                  >
                    Verwijder
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {decision.chosen && (
          <div className={styles.chosen}>
            <div className={`eyebrow c-ok ${styles.chosenLabel}`}>Gekozen</div>
            <div className={styles.chosenText}>{decision.chosen}</div>
          </div>
        )}
        <div className={styles.section}>
          <div className={`eyebrow ${styles.sectionLabel}`}>Overwogen alternatieven</div>
          <div className="col gap-4">
            {decision.options
              .filter((o) => o !== decision.chosen)
              .map((a) => (
                <div key={a} className={`row gap-6 ${styles.alt}`}>
                  <span className={styles.altBullet} />
                  <span>{a}</span>
                </div>
              ))}
          </div>
        </div>
        {/* "Betrokken" footer removed 2026-05-17 — the API does not model
            decision participants, so the previously hardcoded Marc/Jan/Lieve
            avatars were fictional. Reintroduce when a real participants
            relation exists on the Decision entity. */}
      </div>
    </div>
  );
}

function groupByPhase(items: DecisionDto[]): Map<string, DecisionDto[]> {
  const out = new Map<string, DecisionDto[]>();
  for (const d of items) {
    const arr = out.get(d.phaseId) ?? [];
    arr.push(d);
    out.set(d.phaseId, arr);
  }
  return out;
}
